import { ActivatedRouteSnapshot, CanActivateFn, Router, UrlTree } from '@angular/router';
import { combineLatest, filter, map, Observable, of, take, timeout } from 'rxjs';
import { inject } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { MembershipModel, Organization, OrganizationPermission, Permission } from '@shared/models';
import { isAdmin, isOrganizationRestricted } from '@shared/helpers';
import { MembershipsStore, OrganizationsStore } from '@shared/state';
import { ROUTE_NAMES } from '@brc/route-names';

const DATA_WAIT_TIMEOUT_MS = 5000;

/** Admins are never restricted. */
function isRestricted(organization: Organization, permission: Permission | undefined): boolean {
  return !(permission && isAdmin(permission)) && isOrganizationRestricted(organization);
}

function checkAccess(
  organization: Organization,
  permission: Permission | undefined,
  orgId: number,
  router: Router
): boolean | UrlTree {
  return isRestricted(organization, permission) ? router.createUrlTree([`${ROUTE_NAMES.OVERVIEW}/${orgId}`]) : true;
}

/** Resolves access from the selected organization; allows navigation when none is selected. */
function checkSelectedOrganizationAccess(router: Router, selected: OrganizationPermission | undefined): boolean | UrlTree {
  if (!selected?.organization) {
    return true;
  }
  return checkAccess(selected.organization, selected.permission, selected.organization.id, router);
}

/** Resolves access for the route organization, falling back to the selected organization when it is not in state. */
function checkRouteOrganizationAccess(
  router: Router,
  orgId: number,
  organizations: Organization[],
  memberships: MembershipModel[],
  selected: OrganizationPermission | undefined
): boolean | UrlTree {
  const organization = organizations.find((org) => org.id === orgId);
  if (!organization) {
    return checkSelectedOrganizationAccess(router, selected);
  }
  const membership = memberships.find((m) => m.organization.name === organization.name);
  return checkAccess(organization, membership?.role.name as Permission, orgId, router);
}

/**
 * Restricts non-admins to Overview when their organization is on Tier1 or has an inactive license.
 * Uses loaded state when available; otherwise waits up to 5 s for data (fresh page load or deep link).
 */
export const SubscriptionPlanGuard: CanActivateFn = (
  route: ActivatedRouteSnapshot
): Observable<boolean | UrlTree> | boolean | UrlTree => {
  const router = inject(Router);
  const membershipsStore = inject(MembershipsStore);
  const organizationsStore = inject(OrganizationsStore);

  const orgIdParam = route.params['organizationId'];
  if (!orgIdParam) {
    return checkSelectedOrganizationAccess(router, organizationsStore.selected());
  }

  const orgId = Number(orgIdParam);
  const organizations = organizationsStore.organizations();
  const memberships = membershipsStore.memberships();

  if (organizations?.length && memberships?.length) {
    return checkRouteOrganizationAccess(router, orgId, organizations, memberships, organizationsStore.selected());
  }

  return combineLatest([
    toObservable(organizationsStore.organizations).pipe(filter((orgs): orgs is Organization[] => !!orgs?.length)),
    toObservable(membershipsStore.memberships).pipe(filter((mems): mems is MembershipModel[] => !!mems?.length)),
  ]).pipe(
    take(1),
    timeout({ first: DATA_WAIT_TIMEOUT_MS, with: () => of(undefined) }),
    map((loaded) =>
      loaded ? checkRouteOrganizationAccess(router, orgId, loaded[0], loaded[1], organizationsStore.selected()) : true
    )
  );
};
